import { Link } from "react-router";

export interface Agreement {
  id: string;
  name: string;
  agreementType: string;
  agreementTypeId: string | null;
  state: string | null;
  jurisdictionKind: string;
  jurisdictionValue: string | null;
  isActive: boolean;
  headerHtml?: string | null;
  footerHtml?: string | null;
  countersignerName?: string | null;
  countersignerSignatureKey?: string | null;
  marginTop?: number | null;
  marginBottom?: number | null;
  marginLeft?: number | null;
  marginRight?: number | null;
  topLeftHtml?: string | null;
  topRightHtml?: string | null;
  showFirstPageMargins?: boolean | null;
  signatureBlockHtml?: string | null;
  previewImageUrl?: string | null;
  previewGeneratedAt?: string | null;
  createdAt?: string | null;
}

function detectOverrides(a: Agreement): string[] {
  const zones: string[] = [];
  if (a.headerHtml != null) zones.push("Header");
  if (a.footerHtml != null) zones.push("Footer");
  if (a.countersignerName != null || a.countersignerSignatureKey != null)
    zones.push("Countersigner");
  if (
    a.marginTop != null ||
    a.marginBottom != null ||
    a.marginLeft != null ||
    a.marginRight != null
  )
    zones.push("Margins");
  if (a.topLeftHtml != null || a.topRightHtml != null)
    zones.push("Page margins");
  if (a.signatureBlockHtml != null) zones.push("Signature block");
  return zones;
}

function humanize(s: string): string {
  return s.replace(/_/g, " ").replace(/\b\w/g, (c) => c.toUpperCase());
}

function titleCase(s: string): string {
  return s.replace(/\b\w/g, (c) => c.toUpperCase()).replace(/\B\w+/g, (w) => w.toLowerCase());
}

function jurisdictionLabel(a: Agreement): string {
  const kind = a.jurisdictionKind ?? "national";
  if (kind === "national") return "National";
  if (kind === "state") return a.state || "State";
  const value = a.jurisdictionValue
    ? titleCase(a.jurisdictionValue.toLowerCase())
    : "";
  const state = a.state || "";
  if (value && state) return `${value}, ${state}`;
  return value || state || humanize(kind);
}

function formatDate(iso: string | null | undefined): string {
  if (!iso) return "";
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return "";
  return d.toLocaleDateString(undefined, { month: "short", day: "numeric" });
}

function PlaceholderDocPage({ name }: { name: string }) {
  // Pure-CSS document placeholder mimicking the v2 mock.
  return (
    <div className="w-[110px]">
      <div
        className="bg-paper-hi rounded-sm relative"
        style={{
          aspectRatio: "8.5 / 11",
          boxShadow:
            "0 0 0 1px rgba(11,37,69,0.08), 0 12px 24px -8px rgba(11,37,69,0.10)",
        }}
        aria-hidden="true"
      >
        <div className="absolute top-[6%] left-[8%] right-[8%] hair-b border-b pb-1 flex items-center gap-1">
          <div className="w-3 h-3 rounded-sm bg-ink-2"></div>
          <div className="text-[6px] font-medium">Tenant</div>
        </div>
        <div className="absolute top-[20%] left-[8%] right-[8%] space-y-0.5">
          <div className="font-display text-[7px] font-medium truncate">
            {name}
          </div>
          <div className="h-[3px] bg-ink-6/40 rounded w-[96%] mt-1"></div>
          <div className="h-[3px] bg-ink-6/40 rounded w-[96%]"></div>
          <div className="h-[3px] bg-ink-6/40 rounded w-[78%]"></div>
          <div className="h-[3px] bg-ink-6/40 rounded w-[96%]"></div>
          <div className="h-[3px] bg-ink-6/40 rounded w-[52%]"></div>
          <div className="h-[3px] bg-ink-6/40 rounded w-[96%]"></div>
        </div>
        <div className="absolute bottom-[6%] left-[8%] right-[8%] hair-t border-t pt-0.5 text-[5px] text-ink-4 flex justify-between">
          <span>Confidential</span>
          <span>Pg 1</span>
        </div>
      </div>
    </div>
  );
}

interface AgreementCardProps {
  agreement: Agreement;
}

export function AgreementCard({ agreement }: AgreementCardProps) {
  const overrides = detectOverrides(agreement);
  const hasOverrides = overrides.length > 0;
  const inactive = !agreement.isActive;

  return (
    <Link
      to={`/settings/agreements/${agreement.id}`}
      className={`bg-paper-hi rounded-lg shadow-card hover:shadow-lift hover:-translate-y-0.5 transition-[transform,box-shadow] duration-200 ease-out overflow-hidden flex flex-col group ${
        inactive ? "opacity-70" : ""
      }`}
    >
      <div className="bg-canvas/50 p-5 flex items-center justify-center relative overflow-hidden">
        <div className="transition-transform duration-300 ease-out group-hover:scale-[1.04] group-hover:-rotate-[0.5deg]">
          {agreement.previewImageUrl ? (
            <img
              src={agreement.previewImageUrl}
              alt=""
              width={110}
              height={142}
              loading="lazy"
              className="w-[110px] h-auto rounded-sm"
              style={{
                boxShadow:
                  "0 0 0 1px rgba(11,37,69,0.08), 0 12px 24px -8px rgba(11,37,69,0.10)",
              }}
            />
          ) : (
            <PlaceholderDocPage name={agreement.name} />
          )}
        </div>
        {hasOverrides && (
          <span
            className="absolute top-3 right-3 bg-spark text-paper-hi rounded-full text-[10px] font-medium px-2 py-0.5"
            aria-label={`${overrides.length} design ${
              overrides.length === 1 ? "override" : "overrides"
            }`}
          >
            {overrides.length}{" "}
            {overrides.length === 1 ? "override" : "overrides"}
          </span>
        )}
      </div>

      <div className="px-4 py-3 hair-t border-t flex-1">
        <div className="flex items-start justify-between gap-2">
          <div className="min-w-0">
            <div className="text-[13.5px] font-medium truncate">
              {agreement.name}
            </div>
            <div className="text-[11.5px] text-ink-4 mt-0.5 truncate">
              {jurisdictionLabel(agreement)}
            </div>
          </div>
          <span
            className={`inline-flex items-center gap-1.5 text-[11px] font-medium flex-shrink-0 ${
              agreement.isActive ? "text-sage" : "text-ink-4"
            }`}
          >
            <span
              className={`dot ${
                agreement.isActive ? "bg-sage" : "bg-ink-5"
              }`}
            ></span>
            {agreement.isActive ? "Active" : "Inactive"}
          </span>
        </div>

        <div className="hair-t border-t mt-3 pt-2 flex items-center justify-between text-[10.5px]">
          {hasOverrides ? (
            <span className="text-spark font-medium truncate">
              {overrides.join(" · ")} overridden
            </span>
          ) : (
            <span className="text-ink-4">Uses default design</span>
          )}
          <span className="text-ink-5 num flex-shrink-0 ml-2">
            {formatDate(agreement.createdAt)}
          </span>
        </div>
      </div>
    </Link>
  );
}
